import { useState, useEffect, useCallback, useRef } from 'react';
import { Info, Check, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// MOCK DATA
// ============================================================================

interface MockGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeSeed: number | null;
  awaySeed: number | null;
  homeScore: number | null;
  awayScore: number | null;
  winner: 'HOME' | 'AWAY' | null;
  gameTime: string;
}

interface MockProp {
  id: string;
  description: string;
  costCoins: number;
  bonusMultiplier: number;
  category: string;
  line: number | null;
  gameId: string;
  side?: 'OVER' | 'UNDER';
}

interface MockMyProp {
  undercardPropId: string;
  result: 'PENDING' | 'HIT' | 'MISS' | null;
  bonusPoints: number;
}

type PickSelection = 'HOME' | 'AWAY';

const MOCK_GAMES: MockGame[] = [
  {
    id: 'game-1',
    homeTeam: 'UConn Huskies',
    awayTeam: 'Michigan St Spartans',
    homeSeed: 1,
    awaySeed: 9,
    homeScore: null,
    awayScore: null,
    winner: null,
    gameTime: '2026-03-28T18:00:00Z',
  },
  {
    id: 'game-2',
    homeTeam: 'Duke Blue Devils',
    awayTeam: 'Arkansas Razorbacks',
    homeSeed: 2,
    awaySeed: 4,
    homeScore: 82,
    awayScore: 75,
    winner: 'HOME',
    gameTime: '2026-03-28T20:00:00Z',
  },
];

/**
 * Generate spread props per team with full range, primary in the middle.
 * Home is favorite at -5.5 primary. Away is underdog at +5.5 primary.
 *
 * Home: -1.5(easy) → -5.5*(primary) → -10.5(hard)
 * Away: +10.5(easy) → +5.5*(primary) → +1.5(hard)
 */
function generateSpreadProps(gameId: string, homeTeam: string, awayTeam: string): MockProp[] {
  const primarySpread = 5.5;
  const props: MockProp[] = [];

  // Home (favorite): -1.5, -3.5, -5.5*, -7.5, -10.5
  const homeLines = [-1.5, -3.5, -5.5, -7.5, -10.5];
  for (const line of homeLines) {
    const dist = Math.abs(line) - primarySpread; // negative = easier, positive = harder
    const mult = dist >= 0
      ? Math.round((1.0 + dist * 0.3) * 100) / 100
      : Math.round(Math.max(0.4, 1.0 + dist * 0.1) * 100) / 100;
    const cost = Math.max(15, Math.round(50 * mult / 5) * 5);
    props.push({
      id: `${gameId}-spread-home-${line}`,
      description: `${homeTeam} ${line} (spread)`,
      costCoins: cost,
      bonusMultiplier: mult,
      category: 'SPREAD',
      line,
      gameId,
    });
  }

  // Away (underdog): +1.5, +3.5, +5.5*, +7.5, +10.5
  const awayLines = [1.5, 3.5, 5.5, 7.5, 10.5];
  for (const line of awayLines) {
    const dist = primarySpread - line; // positive = harder, negative = easier
    const mult = dist >= 0
      ? Math.round((1.0 + dist * 0.3) * 100) / 100
      : Math.round(Math.max(0.4, 1.0 + dist * 0.1) * 100) / 100;
    const cost = Math.max(15, Math.round(50 * mult / 5) * 5);
    const sign = line > 0 ? '+' : '';
    props.push({
      id: `${gameId}-spread-away-${line}`,
      description: `${awayTeam} ${sign}${line} (spread)`,
      costCoins: cost,
      bonusMultiplier: mult,
      category: 'SPREAD',
      line,
      gameId,
    });
  }

  return props;
}

/**
 * Generate total props with full range in both directions.
 * Primary line is in the middle of the slider.
 *
 * Over: lower line = easier (less bonus), higher line = harder (more bonus)
 * Under: higher line = easier (less bonus), lower line = harder (more bonus)
 */
function generateTotalProps(gameId: string, primaryLine: number): MockProp[] {
  const offsets = [-8, -6, -4, -2, 0, 2, 4, 6, 8];
  const props: MockProp[] = [];
  for (const off of offsets) {
    const line = primaryLine + off;
    // Over: going UP from primary = harder = more bonus; going DOWN = easier = less bonus
    // Bonus range: 0.4x (easiest) → 1.0x (primary) → 3.0x (hardest)
    const overMult = off >= 0
      ? Math.round((1.0 + off * 0.25) * 100) / 100      // above primary: 1.0 → 3.0
      : Math.round(Math.max(0.4, 1.0 + off * 0.08) * 100) / 100; // below primary: 1.0 → 0.36 (clamped 0.4)

    // Under: going DOWN from primary = harder = more bonus; going UP = easier = less bonus
    const underMult = off <= 0
      ? Math.round((1.0 + Math.abs(off) * 0.25) * 100) / 100  // below primary: 1.0 → 3.0
      : Math.round(Math.max(0.4, 1.0 - off * 0.08) * 100) / 100; // above primary: 1.0 → 0.36 (clamped 0.4)

    props.push({
      id: `${gameId}-total-over-${line}`,
      description: `Total Over ${line}`,
      costCoins: Math.max(15, Math.round(50 * overMult / 5) * 5),
      bonusMultiplier: overMult,
      category: 'TOTAL_OVER',
      line,
      gameId,
    });
    props.push({
      id: `${gameId}-total-under-${line}`,
      description: `Total Under ${line}`,
      costCoins: Math.max(15, Math.round(50 * underMult / 5) * 5),
      bonusMultiplier: underMult,
      category: 'TOTAL_UNDER',
      line,
      gameId,
    });
  }
  return props;
}

const PLAYER_CATEGORY_LABELS: Record<string, string> = {
  PLAYER_POINTS: 'Points',
  PLAYER_REBOUNDS: 'Rebounds',
  PLAYER_ASSISTS: 'Assists',
  PLAYER_POINTS_REBOUNDS: 'Pts+Reb',
  PLAYER_POINTS_ASSISTS: 'Pts+Ast',
  PLAYER_REBOUNDS_ASSISTS: 'Reb+Ast',
};

function makePlayerProps(gameId: string, playerName: string, cats: { cat: string; lines: number[]; mainOnly?: boolean }[]): MockProp[] {
  const props: MockProp[] = [];
  for (const { cat, lines, mainOnly } of cats) {
    const primaryIdx = Math.floor(lines.length / 2);
    const primaryLine = lines[primaryIdx];
    const linesToUse = mainOnly ? [primaryLine] : lines;

    for (const line of linesToUse) {
      const off = line - primaryLine;
      const overMult = off >= 0
        ? Math.round((1.0 + off * 0.15) * 100) / 100
        : Math.round(Math.max(0.4, 1.0 + off * 0.06) * 100) / 100;
      const underMult = off <= 0
        ? Math.round((1.0 + Math.abs(off) * 0.15) * 100) / 100
        : Math.round(Math.max(0.4, 1.0 - off * 0.06) * 100) / 100;

      props.push({
        id: `${gameId}-${playerName}-${cat}-over-${line}`,
        description: `${playerName} \u2014 ${PLAYER_CATEGORY_LABELS[cat] || cat} Over ${line}`,
        costCoins: Math.max(15, Math.round(50 * overMult / 5) * 5),
        bonusMultiplier: overMult,
        category: cat,
        line,
        gameId,
        side: 'OVER' as const,
      });
      props.push({
        id: `${gameId}-${playerName}-${cat}-under-${line}`,
        description: `${playerName} \u2014 ${PLAYER_CATEGORY_LABELS[cat] || cat} Under ${line}`,
        costCoins: Math.max(15, Math.round(50 * underMult / 5) * 5),
        bonusMultiplier: underMult,
        category: cat,
        line,
        gameId,
        side: 'UNDER' as const,
      });
    }
  }
  return props;
}

const MOCK_GAME_PROPS: MockProp[] = [
  ...generateSpreadProps('game-1', 'UConn Huskies', 'Michigan St Spartans'),
  ...generateTotalProps('game-1', 147.5),
  ...generateSpreadProps('game-2', 'Duke Blue Devils', 'Arkansas Razorbacks'),
  ...generateTotalProps('game-2', 155.5),
];

const MOCK_PLAYER_PROPS: MockProp[] = [
  // UConn players
  ...makePlayerProps('game-1', 'Donovan Clingan', [
    { cat: 'PLAYER_POINTS', lines: [12.5, 14.5, 16.5, 18.5, 20.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [6.5, 8.5, 10.5, 12.5] },
    { cat: 'PLAYER_ASSISTS', lines: [1.5, 2.5, 3.5] },
  ]),
  ...makePlayerProps('game-1', 'Tristen Newton', [
    { cat: 'PLAYER_POINTS', lines: [10.5, 12.5, 14.5, 16.5] },
    { cat: 'PLAYER_ASSISTS', lines: [3.5, 4.5, 5.5, 6.5] },
  ]),
  ...makePlayerProps('game-1', 'Stephon Castle', [
    { cat: 'PLAYER_POINTS', lines: [8.5, 10.5, 12.5, 14.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [3.5], mainOnly: true },
    { cat: 'PLAYER_POINTS_REBOUNDS', lines: [14.5], mainOnly: true },
  ]),
  // Duke players
  ...makePlayerProps('game-2', 'Kyle Filipowski', [
    { cat: 'PLAYER_POINTS', lines: [14.5, 16.5, 18.5, 20.5, 22.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [6.5, 8.5, 10.5] },
    { cat: 'PLAYER_ASSISTS', lines: [1.5, 2.5, 3.5] },
  ]),
  ...makePlayerProps('game-2', 'Jared McCain', [
    { cat: 'PLAYER_POINTS', lines: [10.5, 12.5, 14.5, 16.5] },
    { cat: 'PLAYER_ASSISTS', lines: [2.5, 3.5, 4.5] },
  ]),
];

const MOCK_PROPS: MockProp[] = [...MOCK_GAME_PROPS, ...MOCK_PLAYER_PROPS];

const MOCK_MY_PROPS: MockMyProp[] = [
  { undercardPropId: 'game-2-Kyle Filipowski-PLAYER_POINTS-over-18.5', result: 'HIT', bonusPoints: 3.5 },
];

// ============================================================================
// SHARED COMPONENTS (mirrors page.tsx)
// ============================================================================

function formatLine(line: number | null): string {
  if (line === null) return '\u2014';
  return line > 0 ? `+${line}` : `${line}`;
}

/** Derive a short abbreviation from a team name (e.g. "Michigan St Spartans" → "MSU") */
const TEAM_ABBREVS: Record<string, string> = {
  'UConn Huskies': 'UCONN',
  'Michigan St Spartans': 'MSU',
  'Duke Blue Devils': 'DUKE',
  'Arkansas Razorbacks': 'ARK',
  'North Carolina Tar Heels': 'UNC',
  'Kentucky Wildcats': 'UK',
  'Kansas Jayhawks': 'KU',
  'Gonzaga Bulldogs': 'GONZ',
};

function getAbbrev(teamName: string): string {
  if (TEAM_ABBREVS[teamName]) return TEAM_ABBREVS[teamName];
  // Fallback: first word, max 4 chars uppercase
  const words = teamName.split(' ');
  return words[0].slice(0, 4).toUpperCase();
}

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full border border-warm-gray/40 text-warm-gray/60 hover:text-warm-gray transition-colors"
        aria-label="Info"
      >
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 px-3 py-2 rounded-lg bg-surface-3 border border-surface-4 text-[11px] text-cream/80 shadow-lg leading-snug">
          {text}
        </div>
      )}
    </div>
  );
}

function GameWinnerRow({ game, pick, onPick, disabled, pointsPerCorrectPick }: {
  game: MockGame;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  disabled: boolean;
  pointsPerCorrectPick: number;
}) {
  const settled = game.winner !== null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Game Winner</span>
        <span className="text-[10px] text-warm-gray/40">({pointsPerCorrectPick} points if correct)</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPick(game.id, 'AWAY')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'AWAY'
              ? settled
                ? game.winner === 'AWAY' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span>
            {game.awaySeed && <span className="text-warm-gray/60 mr-1">({game.awaySeed})</span>}
            {game.awayTeam}
          </span>
          {settled && game.awayScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.awayScore})</span>
          )}
        </button>

        <div className="w-8 text-center flex-shrink-0">
          {settled ? (
            pick ? (
              game.winner === pick ? (
                <Check className="w-4 h-4 text-verdigris mx-auto" />
              ) : (
                <X className="w-4 h-4 text-oxblood mx-auto" />
              )
            ) : (
              <span className="text-xs text-tertiary">&mdash;</span>
            )
          ) : (
            <span className="text-[10px] text-tertiary font-bold">or</span>
          )}
        </div>

        <button
          onClick={() => onPick(game.id, 'HOME')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'HOME'
              ? settled
                ? game.winner === 'HOME' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span>
            {game.homeSeed && <span className="text-warm-gray/60 mr-1">({game.homeSeed})</span>}
            {game.homeTeam}
          </span>
          {settled && game.homeScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.homeScore})</span>
          )}
        </button>
      </div>
    </div>
  );
}

function SpreadSection({ game, props: spreadProps, onPurchase, disabled }: {
  game: MockGame;
  props: MockProp[];
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [team, setTeam] = useState<'AWAY' | 'HOME'>('AWAY');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, team]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  const selectedTeam = team === 'AWAY' ? game.awayTeam : game.homeTeam;
  const selectedSeed = team === 'AWAY' ? game.awaySeed : game.homeSeed;
  const teamProps = spreadProps.filter((p) => p.description.includes(selectedTeam));
  // Sort by line so slider represents the number line; default starts at primary (1.0x)
  const sorted = [...teamProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const displayProps = sorted.filter((p) => p.line !== null);

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const showSlider = new Set(displayProps.map((p) => p.line)).size >= 2;

  if (disabled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
        </div>
        {currentProp && (
          <div className="flex items-center gap-2 text-[10px] text-warm-gray/40">
            <span>
              {selectedSeed && <span className="mr-1">({selectedSeed})</span>}
              {selectedTeam}
            </span>
            <span>{formatLine(currentProp.line)}</span>
            <span>{currentProp.bonusMultiplier}x</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
        <InfoTooltip text="Pick an alternate point spread — the further from the primary line, the bigger the payout." />
      </div>

      {/* Team selector toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => { setTeam('AWAY'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            team === 'AWAY'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          {getAbbrev(game.awayTeam)}
        </button>
        <button
          onClick={() => { setTeam('HOME'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            team === 'HOME'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          {getAbbrev(game.homeTeam)}
        </button>
      </div>

      {currentProp && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-cream whitespace-nowrap">{formatLine(currentProp.line)}</span>

          {showSlider && (
            <input
              type="range"
              min={0}
              max={displayProps.length - 1}
              value={Math.min(effectiveIndex, displayProps.length - 1)}
              onChange={(e) => setSliderIdx(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer min-w-[60px]
                bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
                [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
                [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
            />
          )}

          <span className="text-[11px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>

          {confirmPropId === currentProp.id ? (
            <button
              onClick={() => handleBuyClick(currentProp.id)}
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
            >
              Confirm {currentProp.costCoins}c
            </button>
          ) : (
            <button
              onClick={() => handleBuyClick(currentProp.id)}
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30"
            >
              Buy {currentProp.costCoins}c
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TotalSection({ props: totalProps, onPurchase, disabled }: {
  props: MockProp[];
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [side, setSide] = useState<'OVER' | 'UNDER'>('OVER');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, side]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  const sideProps = totalProps.filter((p) =>
    side === 'OVER' ? p.category === 'TOTAL_OVER' : p.category === 'TOTAL_UNDER'
  );
  // Sort by line so slider represents the number line; default starts at primary (1.0x)
  const sorted = [...sideProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const displayProps = sorted.filter((p) => p.line !== null);

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const showSlider = new Set(displayProps.map((p) => p.line)).size >= 2;

  if (disabled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Total Goals</span>
        </div>
        {currentProp && (
          <div className="flex items-center gap-2 text-[10px] text-warm-gray/40">
            <span>O/U {currentProp.line}</span>
            <span>{currentProp.bonusMultiplier}x</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Total Goals</span>
        <InfoTooltip text="Predict whether the combined score goes over or under the line." />
      </div>

      <div className="flex gap-1">
        <button
          onClick={() => { setSide('OVER'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            side === 'OVER'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          Over
        </button>
        <button
          onClick={() => { setSide('UNDER'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            side === 'UNDER'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          Under
        </button>
      </div>

      {currentProp && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-cream whitespace-nowrap">O/U {currentProp.line ?? '\u2014'}</span>

            {showSlider && (
              <input
                type="range"
                min={0}
                max={displayProps.length - 1}
                value={Math.min(effectiveIndex, displayProps.length - 1)}
                onChange={(e) => setSliderIdx(Number(e.target.value))}
                className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer min-w-[60px]
                  bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
                  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
                  [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5
                  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
                  [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
              />
            )}

            <span className="text-[11px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>

            {confirmPropId === currentProp.id ? (
              <button
                onClick={() => handleBuyClick(currentProp.id)}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
              >
                Confirm {currentProp.costCoins}c
              </button>
            ) : (
              <button
                onClick={() => handleBuyClick(currentProp.id)}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30"
              >
                Buy {currentProp.costCoins}c
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function extractPlayerName(description: string): string {
  const dashIdx = description.indexOf(' \u2014 ');
  if (dashIdx > 0) return description.substring(0, dashIdx);
  return 'Unknown';
}

function fmtPts(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

/** Single player prop category — O/U toggle + slider (or main-line-only simplified buy) */
function PlayerCategoryRow({ category, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  category: string;
  props: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [side, setSide] = useState<'OVER' | 'UNDER'>('OVER');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, side]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  const sideProps = props.filter((p) => p.side === side);
  const sorted = [...sideProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const displayProps = sorted.filter((p) => p.line !== null);
  const uniqueLines = new Set(displayProps.map((p) => p.line));
  const isMainLineOnly = uniqueLines.size <= 1;
  const hasAltLines = uniqueLines.size >= 2;

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = props.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased ? props.find((p) => purchasedPropIds.has(p.id)) : null;
  const purchasedData = purchasedProp ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  const label = PLAYER_CATEGORY_LABELS[category] || category;

  if (!currentProp && !purchasedProp) return null;

  // Purchased state
  if (hasPurchased && purchasedProp) {
    const purchasedSide = purchasedProp.side === 'OVER' ? 'Over' : purchasedProp.side === 'UNDER' ? 'Under' : '';
    return (
      <div className="flex items-center gap-2 text-xs py-1.5">
        <span className="text-warm-gray/60 text-[10px] font-medium">{label}</span>
        {purchasedSide && <span className="text-warm-gray/50 text-[10px]">{purchasedSide}</span>}
        <span className="font-bold text-cream">{purchasedProp.line}</span>
        <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
        <span className={cn(
          'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
          isResolved
            ? purchasedData?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris' : 'bg-oxblood/20 text-oxblood'
            : 'bg-gold/10 text-gold',
        )}>
          {isResolved
            ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
            : 'Purchased'}
        </span>
      </div>
    );
  }

  // Locked
  if (disabled) {
    return (
      <div className="flex items-center gap-2 text-[10px] text-warm-gray/40 py-1">
        <span className="font-medium">{label}</span>
        <span>O/U {currentProp.line}</span>
        <span>{currentProp.bonusMultiplier}x</span>
      </div>
    );
  }

  // Main line only — simplified buy
  if (isMainLineOnly) {
    return (
      <div className="py-1.5 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
          <span className="text-[10px] text-warm-gray/30 italic">main line</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setSide('OVER')} className={cn('px-2 py-0.5 rounded text-[10px] font-bold transition-all', side === 'OVER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>O</button>
          <button onClick={() => setSide('UNDER')} className={cn('px-2 py-0.5 rounded text-[10px] font-bold transition-all', side === 'UNDER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>U</button>
          <span className="text-xs font-bold text-cream">{currentProp.line}</span>
          <span className="text-[10px] text-warm-gray/50">{currentProp.bonusMultiplier}x</span>
          <span className="flex-1" />
          {confirmPropId === currentProp.id ? (
            <button onClick={() => handleBuyClick(currentProp.id)} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap">Confirm {currentProp.costCoins}c</button>
          ) : (
            <button onClick={() => handleBuyClick(currentProp.id)} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30">{currentProp.costCoins}c</button>
          )}
        </div>
      </div>
    );
  }

  // Alt lines — O/U toggle + slider
  return (
    <div className="py-1.5 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
      </div>
      <div className="flex gap-1">
        <button onClick={() => { setSide('OVER'); setSliderIdx(-1); }} className={cn('flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all', side === 'OVER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>Over</button>
        <button onClick={() => { setSide('UNDER'); setSliderIdx(-1); }} className={cn('flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all', side === 'UNDER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>Under</button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-cream whitespace-nowrap">{currentProp.line ?? '\u2014'}</span>
        {hasAltLines && (
          <input
            type="range" min={0} max={displayProps.length - 1}
            value={Math.min(effectiveIndex, displayProps.length - 1)}
            onChange={(e) => setSliderIdx(Number(e.target.value))}
            className="flex-1 h-1 rounded-full appearance-none cursor-pointer min-w-[40px]
              bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
              [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
              [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
              [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
          />
        )}
        <span className="text-[10px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>
        {confirmPropId === currentProp.id ? (
          <button onClick={() => handleBuyClick(currentProp.id)} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap">Confirm {currentProp.costCoins}c</button>
        ) : (
          <button onClick={() => handleBuyClick(currentProp.id)} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30">{currentProp.costCoins}c</button>
        )}
      </div>
    </div>
  );
}

/** Collapsible player card within game card */
function InlinePlayerCard({ playerName, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  playerName: string;
  props: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const purchasedCount = props.filter((p) => purchasedPropIds.has(p.id)).length;

  // Group by category
  const byCategory = new Map<string, MockProp[]>();
  for (const prop of props) {
    const cat = prop.category || 'OTHER';
    const arr = byCategory.get(cat) || [];
    arr.push(prop);
    byCategory.set(cat, arr);
  }

  const categoryOrder = ['PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS', 'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS'];
  const sortedCategories = [...byCategory.entries()].sort((a, b) => {
    const ai = categoryOrder.indexOf(a[0]);
    const bi = categoryOrder.indexOf(b[0]);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // "Last, First" format
  const parts = playerName.split(' ');
  const displayName = parts.length >= 2
    ? `${parts.slice(-1)[0]}, ${parts.slice(0, -1).join(' ')}`
    : playerName;

  const pointsProp = props.find((p) => p.category === 'PLAYER_POINTS' && p.side === 'OVER');
  const previewLine = pointsProp ? `${pointsProp.line} goals` : `${sortedCategories.length} cats`;

  const mainOnlyCount = sortedCategories.filter(([, catProps]) => {
    const overProps = catProps.filter((p) => p.side === 'OVER');
    return new Set(overProps.map((p) => p.line)).size <= 1;
  }).length;

  return (
    <div className="border-b border-surface-3/50 last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-surface-3/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={cn('w-3 h-3 text-warm-gray/40 transition-transform', !expanded && '-rotate-90')} />
          <span className="text-cream text-xs font-medium">{displayName}</span>
          <span className="text-[10px] text-warm-gray/40">{previewLine}</span>
        </div>
        <div className="flex items-center gap-2">
          {purchasedCount > 0 && <span className="text-[10px] text-gold font-medium">{purchasedCount} bought</span>}
          {mainOnlyCount > 0 && purchasedCount === 0 && <span className="text-[9px] text-warm-gray/30 italic">{mainOnlyCount} main only</span>}
          <span className="text-[10px] text-warm-gray/30">{sortedCategories.length}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-2 space-y-0.5">
          {sortedCategories.map(([category, catProps]) => (
            <PlayerCategoryRow
              key={category}
              category={category}
              props={catProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={onPurchase}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UnifiedGameCard({ game, pick, onPick, picksDisabled, pointsPerCorrectPick, props: gameProps, playerProps, myProps, purchasedPropIds, onPurchase, propDisabled }: {
  game: MockGame;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  picksDisabled: boolean;
  pointsPerCorrectPick: number;
  props: MockProp[];
  playerProps: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  propDisabled: boolean;
}) {
  const [playerPropsExpanded, setPlayerPropsExpanded] = useState(false);

  const spreadProps = gameProps.filter((p) => p.category === 'SPREAD');
  const totalProps = gameProps.filter((p) => p.category === 'TOTAL_OVER' || p.category === 'TOTAL_UNDER');

  // Group player props by player
  const byPlayer = new Map<string, MockProp[]>();
  for (const prop of playerProps) {
    const name = extractPlayerName(prop.description);
    const arr = byPlayer.get(name) || [];
    arr.push(prop);
    byPlayer.set(name, arr);
  }
  const sortedPlayers = [...byPlayer.entries()].sort((a, b) => {
    const aLine = a[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    const bLine = b[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    return bLine - aLine;
  });
  const playerPropPurchasedCount = playerProps.filter((p) => purchasedPropIds.has(p.id)).length;

  const gameDate = new Date(game.gameTime);
  const timeStr = gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    + ' \u2022 ' + gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden">
      {/* Game header bar */}
      <div className="px-4 py-2.5 bg-surface-3/50 border-b border-surface-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-cream">
            <span>
              {game.awaySeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.awaySeed})</span>}
              {getAbbrev(game.awayTeam)}
            </span>
            <span className="text-warm-gray/40 text-xs font-normal">@</span>
            <span>
              {game.homeSeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.homeSeed})</span>}
              {getAbbrev(game.homeTeam)}
            </span>
          </div>
          <span className="text-[10px] text-warm-gray/50">{timeStr}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
      <GameWinnerRow
        game={game}
        pick={pick}
        onPick={onPick}
        disabled={picksDisabled}
        pointsPerCorrectPick={pointsPerCorrectPick}
      />
      {spreadProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <SpreadSection game={game} props={spreadProps} onPurchase={onPurchase} disabled={propDisabled} />
        </>
      )}
      {totalProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <TotalSection props={totalProps} onPurchase={onPurchase} disabled={propDisabled} />
        </>
      )}
      </div>

      {/* Player Props — collapsible section at bottom */}
      {sortedPlayers.length > 0 && (
        <div className="border-t border-surface-3">
          <button
            onClick={() => setPlayerPropsExpanded(!playerPropsExpanded)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-3/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ChevronDown className={cn('w-3.5 h-3.5 text-warm-gray/40 transition-transform', !playerPropsExpanded && '-rotate-90')} />
              <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Player Props</span>
              <span className="text-[10px] text-warm-gray/40">{sortedPlayers.length} players</span>
            </div>
            <div className="flex items-center gap-2">
              {playerPropPurchasedCount > 0 && (
                <span className="text-[10px] text-gold font-medium">{playerPropPurchasedCount} bought</span>
              )}
            </div>
          </button>
          {playerPropsExpanded && (
            <div>
              {sortedPlayers.map(([playerName, pProps]) => (
                <InlinePlayerCard
                  key={playerName}
                  playerName={playerName}
                  props={pProps}
                  myProps={myProps}
                  purchasedPropIds={purchasedPropIds}
                  onPurchase={onPurchase}
                  disabled={propDisabled}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PREVIEW PAGE
// ============================================================================

export default function UnifiedCardPreview() {
  const [picks, setPicks] = useState<Map<string, PickSelection>>(new Map([
    ['game-2', 'HOME'], // pre-pick Duke for the settled game
  ]));
  const [purchased, setPurchased] = useState<Set<string>>(
    new Set(MOCK_MY_PROPS.map((m) => m.undercardPropId))
  );

  const handlePick = (gameId: string, winner: PickSelection) => {
    setPicks((prev) => {
      const next = new Map(prev);
      if (next.get(gameId) === winner) next.delete(gameId);
      else next.set(gameId, winner);
      return next;
    });
  };

  const handlePurchase = (propId: string) => {
    setPurchased((prev) => new Set([...prev, propId]));
  };

  return (
    <div className="min-h-screen bg-surface text-cream">
      <div className="max-w-sm mx-auto px-3 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-bold text-cream mb-1">Unified GameCard Preview</h1>
          <p className="text-xs text-warm-gray/60">
            Game picks, spreads, totals, and player props — all in one card.
            Expand &ldquo;Player Props&rdquo; at the bottom of each card.
          </p>
        </div>

        {/* Active game */}
        <div>
          <h2 className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-3">Active Game</h2>
          <UnifiedGameCard
            game={MOCK_GAMES[0]}
            pick={picks.get('game-1') ?? null}
            onPick={handlePick}
            picksDisabled={false}
            pointsPerCorrectPick={10}
            props={MOCK_GAME_PROPS.filter((p) => p.gameId === 'game-1')}
            playerProps={MOCK_PLAYER_PROPS.filter((p) => p.gameId === 'game-1')}
            myProps={MOCK_MY_PROPS}
            purchasedPropIds={purchased}
            onPurchase={handlePurchase}
            propDisabled={false}
          />
        </div>

        {/* Settled game */}
        <div>
          <h2 className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-3">Settled Game (Duke won 82-75)</h2>
          <UnifiedGameCard
            game={MOCK_GAMES[1]}
            pick={picks.get('game-2') ?? null}
            onPick={handlePick}
            picksDisabled={true}
            pointsPerCorrectPick={10}
            props={MOCK_GAME_PROPS.filter((p) => p.gameId === 'game-2')}
            playerProps={MOCK_PLAYER_PROPS.filter((p) => p.gameId === 'game-2')}
            myProps={MOCK_MY_PROPS}
            purchasedPropIds={purchased}
            onPurchase={handlePurchase}
            propDisabled={true}
          />
        </div>

        {/* Purchase log */}
        {purchased.size > MOCK_MY_PROPS.length && (
          <div className="rounded-lg bg-surface-3 border border-surface-4 p-3">
            <p className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-2">Purchase Log</p>
            <div className="space-y-1">
              {[...purchased].filter((id) => !MOCK_MY_PROPS.some((m) => m.undercardPropId === id)).map((id) => {
                const prop = MOCK_PROPS.find((p) => p.id === id);
                return prop ? (
                  <div key={id} className="text-[11px] text-cream/70">
                    {prop.description} — {prop.costCoins}c @ {prop.bonusMultiplier}x
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
